import { useState } from 'react';
import { ticketsAPI, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Renegociar la fecha comprometida con quien pidió, **con motivo**.
 *
 * **Por qué no existía.** `fecha_hasta` sólo podía salir del SLA del tipo de pedido. Cuando el área
 * acordaba otra fecha —lo normal en un proyecto— el sistema seguía mostrando y midiendo contra la
 * que nadie prometió: de ese campo salen el semáforo, el orden de la bandeja, el veredicto de
 * plan-vs-compromiso y lo que ve quien pidió. El mecanismo estaba escrito completo en el backend y
 * no tenía un solo llamador; esta pantalla es el llamador que faltaba.
 *
 * **El motivo es obligatorio, y el botón lo dice.** Una promesa cambiada sin razón escrita es una
 * discusión dentro de un mes sin nada a qué volver, y la fecha vieja es la única evidencia de que el
 * compromiso original existió. Es la misma regla que re-estimar una tarea.
 *
 * **Y avisa lo que deja de pasar:** una fecha movida a mano no se recalcula más, ni al reclasificar
 * el caso ni al cambiar el SLA del área. Eso es lo que la persona tiene que saber antes de guardar,
 * no después.
 */
export default function MoverElCompromiso({ tenantId, capsuleId, ticket, onHecho }) {
  const [abierto, setAbierto] = useState(false);
  const [fecha, setFecha] = useState('');
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  // **Un pedido planificado sin fecha no se renegocia: se compromete** (2026-10-09). Es el mismo
  // endpoint; lo que cambia es la pregunta. Se abre con la fecha de fin del plan, que es la que
  // el área ya estimó, y la persona confirma o la corrige.
  const aPlanificar = ticket?.compromiso?.origen === 'a_planificar';
  const finDelPlan = ticket?.compromiso?.plan?.plan_end || '';
  const yaPrometida = ['manual', 'planificado'].includes(ticket?.compromiso?.origen);

  const guardar = async () => {
    setGuardando(true);
    try {
      await ticketsAPI.moverCompromiso(tenantId, capsuleId, ticket.ticket_id, {
        fecha, motivo: motivo.trim(),
      });
      toast.success('Compromiso actualizado');
      setAbierto(false);
      setFecha('');
      setMotivo('');
      await onHecho?.();
    } catch (e) {
      toast.error(formatApiError(e) || 'No se pudo mover la fecha');
    } finally {
      setGuardando(false);
    }
  };

  if (!abierto) {
    return (
      <button
        type="button"
        className="text-[10px] text-brand-primary hover:underline"
        onClick={() => {
          setAbierto(true);
          if (aPlanificar) {
            setFecha(String(finDelPlan).slice(0, 10));
            if (finDelPlan) setMotivo('Se compromete la fecha de fin del plan');
          } else {
            setFecha(String(ticket?.compromiso?.fecha || ticket?.fecha_hasta || '').slice(0, 10));
          }
        }}
        data-testid="abrir-mover-compromiso"
      >
        {aPlanificar ? 'Comprometer la fecha' : 'Renegociar la fecha'}
      </button>
    );
  }

  return (
    <div className="mt-2 border border-brand-border rounded-sm p-2 space-y-2" data-testid="mover-compromiso">
      <p className="text-[10px] text-zinc-600">
        {aPlanificar ? (
          <>
            La fecha que se le promete a quien pidió el caso, y contra la que se va a medir.{' '}
            {finDelPlan
              ? 'Viene propuesta la fecha de fin del plan.'
              : 'Este caso todavía no tiene plan: conviene planificar las tareas primero.'}
          </>
        ) : (
          <>
            La fecha que ve quien pidió el caso. Una vez movida a mano,{' '}
            <b>deja de recalcularse</b> si se reclasifica el caso o cambia el SLA del área.
            {yaPrometida && <> Moverla <b>cuenta como una reprogramación</b>.</>}
          </>
        )}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="text-[10px] uppercase tracking-wider text-zinc-500">
          Nueva fecha
          <Input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="h-8 rounded-sm text-xs mt-0.5"
            data-testid="compromiso-fecha"
          />
        </label>
        <label className="text-[10px] uppercase tracking-wider text-zinc-500">
          Por qué cambia
          <Input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Acordado con el solicitante"
            className="h-8 rounded-sm text-xs mt-0.5"
            data-testid="compromiso-motivo"
          />
        </label>
      </div>
      <div className="flex items-center gap-2">
        {/* Deshabilitado **con su motivo**: un botón que se aprieta y no hace nada manda a la
            persona a concluir que la aplicación está rota. */}
        <Button
          size="sm"
          className="h-7 text-xs rounded-sm"
          disabled={guardando || !fecha || !motivo.trim()}
          title={!motivo.trim() ? 'Hace falta decir por qué cambia la fecha' : undefined}
          onClick={guardar}
          data-testid="compromiso-guardar"
        >
          {guardando ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Guardar'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 text-xs"
          onClick={() => setAbierto(false)}
        >
          Cancelar
        </Button>
      </div>
    </div>
  );
}
